import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, Navigate, Route, Routes, useMatch, useNavigate } from 'react-router-dom';
import { auth } from './api';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { LoginPage } from './pages/LoginPage';
import { SetPasswordPage } from './pages/SetPasswordPage';
import { SitePage } from './pages/SitePage';
import { SitesPage } from './pages/SitesPage';
import { BillingPage } from './pages/BillingPage';
import { NewSitePage } from './pages/NewSitePage';
import { HomeRedirect, SITE_SECTIONS, SiteSwitcher } from './components/SiteSwitcher';

function RequireAuth({ children }: { children: ReactNode }) {
  return auth.signedIn ? <>{children}</> : <Navigate to="/login" replace />;
}

const THEME_KEY = 'sb_console_theme';
type Theme = 'light' | 'dark';
function readTheme(): Theme {
  try {
    return localStorage.getItem(THEME_KEY) === 'dark' ? 'dark' : 'light';
  } catch {
    return 'light';
  }
}

export function App() {
  const navigate = useNavigate();
  const [theme, setTheme] = useState<Theme>(readTheme);
  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {
      /* private mode: theme just does not persist */
    }
  }, [theme]);
  useEffect(() => {
    const onUnauthorised = () => navigate('/login', { state: { notice: 'Your session has ended. Sign in again.' } });
    window.addEventListener('sb:unauthorised', onUnauthorised);
    return () => window.removeEventListener('sb:unauthorised', onUnauthorised);
  }, [navigate]);
  const who = auth.token ? 'Operator' : auth.session?.email;
  const siteMatch = useMatch('/sites/:siteId/*');
  const siteId = siteMatch?.params.siteId && siteMatch.params.siteId !== 'new' ? siteMatch.params.siteId : '';
  return (
    <div className="layout">
      <aside className="sidebar">
        <div className="brand">
          <span className="dot" /> Springboard
        </div>
        {auth.signedIn && <SiteSwitcher />}
        {siteId && (
          <nav className="navlist" aria-label="Site sections">
            {SITE_SECTIONS.map(([slug, label]) => (
              <NavLink key={slug} to={slug ? `/sites/${siteId}/${slug}` : `/sites/${siteId}`} end={!slug} className={({ isActive }) => (isActive ? 'active' : '')}>
                {label}
              </NavLink>
            ))}
          </nav>
        )}
        <nav className="navlist" aria-label="Account" style={{ marginTop: 18 }}>
          <span className="navhead">Account</span>
          <NavLink to="/sites" end className={({ isActive }) => (isActive ? 'active' : '')}>
            All sites
          </NavLink>
          <NavLink to="/billing" className={({ isActive }) => (isActive ? 'active' : '')}>
            Plan &amp; billing
          </NavLink>
        </nav>
        <button type="button" className="theme-switch" data-on={theme === 'dark'} onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')} aria-pressed={theme === 'dark'} title="Switch between the light and dark console theme">
          <span className="track"><span className="knob" /></span>
          Dark mode
        </button>
        <div style={{ marginTop: 32 }} className="small muted">
          {who ? (
            <>
              <div>Signed in as {who}</div>
              <button
                className="btn sm"
                style={{ marginTop: 8 }}
                onClick={() => {
                  auth.signOut();
                  navigate('/login');
                }}
              >
                Sign out
              </button>
            </>
          ) : (
            <NavLink to="/login">Sign in</NavLink>
          )}
        </div>
      </aside>
      <main className="content">
        <Routes>
          <Route
            path="/"
            element={
              <RequireAuth>
                <HomeRedirect />
              </RequireAuth>
            }
          />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/set-password" element={<SetPasswordPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route
            path="/sites"
            element={
              <RequireAuth>
                <SitesPage />
              </RequireAuth>
            }
          />
          <Route
            path="/sites/new"
            element={
              <RequireAuth>
                <NewSitePage />
              </RequireAuth>
            }
          />
          <Route
            path="/billing"
            element={
              <RequireAuth>
                <BillingPage />
              </RequireAuth>
            }
          />
          <Route
            path="/sites/:siteId/*"
            element={
              <RequireAuth>
                <SitePage />
              </RequireAuth>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
}
